import {Component, computed, inject, OnInit, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {finalize} from 'rxjs/operators';
import {PageTitleComponent} from '../../shared/commons/page-title/page-title.component';
import {ThaiDateTimePipe} from '../../shared/commons/thai-datetime.pipe';
import {ToastService} from '../../shared/commons/toast/toast-service';
import {QrTraceConfirmApiService} from './qr-trace-confirm-api.service';
import {
  QrTraceSaleConfirmationItem,
  QrTraceSaleConfirmationStatus
} from '../../../models/generated-models';

type StatusFilter = 'ALL' | QrTraceSaleConfirmationStatus;

interface StatusTab {
  value: StatusFilter;
  label: string;
}

@Component({
  selector: 'app-qr-trace-confirm',
  standalone: true,
  imports: [FormsModule, PageTitleComponent, ThaiDateTimePipe],
  templateUrl: './qr-trace-confirm.html',
  styleUrl: './qr-trace-confirm.scss',
})
export class QrTraceConfirm implements OnInit {
  private readonly api = inject(QrTraceConfirmApiService);
  private readonly toast = inject(ToastService);

  protected readonly statusTabs: StatusTab[] = [
    {value: 'ALL', label: 'ทั้งหมด'},
    {value: 'PENDING', label: 'รอยืนยัน'},
    {value: 'CONFIRMED', label: 'ยืนยันแล้ว'},
    {value: 'REJECTED', label: 'ไม่ยืนยัน'},
  ];

  protected readonly isLoading = signal(false);
  protected readonly searchTerm = signal('');
  protected readonly activeStatus = signal<StatusFilter>('ALL');
  /** id ของคำขอที่กำลังกรอกหมายเหตุก่อนกด "ไม่ยืนยัน" */
  protected readonly rejectingId = signal<number | null>(null);
  protected readonly rejectRemark = signal('');
  protected readonly decidingId = signal<number | null>(null);

  private readonly items = signal<QrTraceSaleConfirmationItem[]>([]);

  protected readonly filteredItems = computed(() => {
    const status = this.activeStatus();
    const term = this.searchTerm().trim().toLowerCase();
    return this.items().filter((item) => {
      if (status !== 'ALL' && item.status !== status) {
        return false;
      }
      if (!term) {
        return true;
      }
      return [
        item.materialName,
        item.plantationName,
        item.plantationNo,
        item.certificateNo,
        item.certificateName,
        item.certificateSubName,
        item.applicantName,
        item.requestId
      ].some((value) => (value ?? '').toLowerCase().includes(term));
    });
  });

  protected readonly pendingCount = computed(
    () => this.items().filter((item) => item.status === 'PENDING').length);

  ngOnInit(): void {
    this.loadItems();
  }

  protected reload(): void {
    this.loadItems();
  }

  protected selectStatus(status: StatusFilter): void {
    this.activeStatus.set(status);
  }

  protected onSearchTermChange(term: string): void {
    this.searchTerm.set(term);
  }

  protected isPending(item: QrTraceSaleConfirmationItem): boolean {
    return item.status === 'PENDING';
  }

  protected statusLabel(status?: QrTraceSaleConfirmationStatus): string {
    switch (status) {
      case 'CONFIRMED':
        return 'ยืนยันจากเกษตรกรแล้ว';
      case 'REJECTED':
        return 'ไม่ยืนยัน';
      default:
        return 'รอยืนยัน';
    }
  }

  protected statusClass(status?: QrTraceSaleConfirmationStatus): string {
    switch (status) {
      case 'CONFIRMED':
        return 'status-confirmed';
      case 'REJECTED':
        return 'status-rejected';
      default:
        return 'status-pending';
    }
  }

  protected onConfirm(item: QrTraceSaleConfirmationItem): void {
    if (item.id == null || this.decidingId() !== null) {
      return;
    }
    const id = item.id;
    this.decidingId.set(id);
    this.api.confirm(id)
      .pipe(finalize(() => this.decidingId.set(null)))
      .subscribe((updated) => {
        this.replaceItem(updated);
        this.toast.success('ยืนยันการจำหน่ายเรียบร้อยแล้ว');
      });
  }

  protected startReject(item: QrTraceSaleConfirmationItem): void {
    if (item.id == null) {
      return;
    }
    this.rejectingId.set(item.id);
    this.rejectRemark.set('');
  }

  protected cancelReject(): void {
    this.rejectingId.set(null);
    this.rejectRemark.set('');
  }

  protected confirmReject(item: QrTraceSaleConfirmationItem): void {
    if (item.id == null || this.decidingId() !== null) {
      return;
    }
    const id = item.id;
    const remark = this.rejectRemark().trim();
    this.decidingId.set(id);
    this.api.reject(id, remark || undefined)
      .pipe(finalize(() => this.decidingId.set(null)))
      .subscribe((updated) => {
        this.replaceItem(updated);
        this.cancelReject();
        this.toast.success('บันทึกการไม่ยืนยันเรียบร้อยแล้ว');
      });
  }

  private loadItems(): void {
    this.isLoading.set(true);
    this.api.list()
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (items) => this.items.set(items),
        error: () => this.items.set([])
      });
  }

  private replaceItem(updated: QrTraceSaleConfirmationItem): void {
    this.items.update((items) => items.map((item) => item.id === updated.id ? updated : item));
  }
}
